import { Line } from 'react-chartjs-2';
import React, { useMemo, useRef, useState } from 'react';
import ChartPointDataModal from './ChartPointDataModal';
import { formatRuDate } from '../utils/ruDateUtils';
const ChartsGroup = ({
  loading,
  chartData,
  dataWindow,
  markerColors,
  getMarkerLabelFunction,
  getTableDataByDate,
  hideDateTitle = false,
  postCards = false,
  showCommentSeries = false,
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState('');
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const detailsRequestId = useRef(0);

  const markerExtents = useMemo(() => {
    const extents = {};
    chartData.forEach(({ marker, data }) => {
      if (Array.isArray(data) && data.length > 0) {
        const values = data
          .map((d) => d.value)
          .filter((v) => typeof v === 'number' && !isNaN(v));
        if (values.length > 0) {
          const minVal = Math.min(...values);
          const maxVal = Math.max(...values);
          const roundToZeroOrFive = (value) => {
            const order = Math.pow(10, Math.floor(Math.log10(value)));
            const normalized = value / order;
            let rounded = Math.ceil(normalized * 1.1);
            if (rounded % 10 === 1 || rounded % 10 === 2)
              rounded = Math.floor(rounded / 10) * 10 + 5;
            else if (rounded % 10 === 3 || rounded % 10 === 4)
              rounded = Math.ceil(rounded / 10) * 10;
            else if (rounded % 10 === 6 || rounded % 10 === 7)
              rounded = Math.floor(rounded / 10) * 10 + 5;
            else if (rounded % 10 === 8 || rounded % 10 === 9)
              rounded = Math.ceil(rounded / 10) * 10;
            const result = rounded * order;
            return Math.max(result, value);
          };
          extents[marker] = {
            min: minVal > 0 ? 0 : Math.floor(minVal * 1.1),
            max: roundToZeroOrFive(maxVal) || 100,
          };
        }
      }
    });
    return extents;
  }, [chartData]);

  const getDataWindowLabel = () => {
    const allDates = chartData.flatMap(({ data }) =>
      (data || []).slice(dataWindow.start, dataWindow.end).map((d) => d.date)
    );
    if (allDates.length === 0) return 'Показаны данные: нет данных';
    const firstDate = allDates[0];
    const lastDate = allDates[allDates.length - 1];
    return `Показаны данные: ${formatRuDate(firstDate)} - ${formatRuDate(lastDate)}`;
  };

  const handlePointClick = async (date, series) => {
    if (!getTableDataByDate) return;
    const requestId = ++detailsRequestId.current;
    setSelectedDate(date);
    setSelectedTitle(series.topicTitle ? `${series.topicTitle} · ${series.marker}` : series.marker);
    setSelectedRows([]);
    setDetailsError(null);
    setDetailsLoading(true);
    try {
      const rows = await getTableDataByDate(date, series);
      if (requestId === detailsRequestId.current) setSelectedRows(rows || []);
    } catch (err) {
      console.error('Ошибка при получении данных по дате:', err);
      if (requestId === detailsRequestId.current) {
        setSelectedRows([]);
        setDetailsError(postCards ? 'Не удалось загрузить посты за этот период' : 'Не удалось загрузить данные за этот период');
      }
    } finally {
      if (requestId === detailsRequestId.current) setDetailsLoading(false);
    }
  };

  return (
    <>
      {/* Заголовок */}
      <div
        style={{
          height: '50px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {loading ? (
          <div
            style={{
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3498db',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              animation: 'spin 1s linear infinite',
            }}
          />
        ) : (
          hideDateTitle ? null : (
          <p style={{ textAlign: 'center', fontWeight: 'bold', color: '#169af7' }}>
            {getDataWindowLabel()}
          </p>)
        )}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>

      {/* Графики */}
      {showCommentSeries && (
        <p style={{ textAlign: 'center', color: '#64748b', fontSize: 13 }}>
          Синяя линия — посты, красная — их сохранённые комментарии и ответы. Период определяется датой поста.
        </p>
      )}
      {chartData.map(({ marker, topicLabel, topicTitle, groupId, data: seriesData }) => {
        const windowedData = (seriesData || []).slice(dataWindow.start, dataWindow.end);
        const extents = markerExtents[marker] || { min: 0, max: 100 };

        const chart = {
          marker,
          data: {
            // Форматируем только отображаемые подписи на оси X,
            // чтобы point.date оставался "ключом" для выборки данных.
            labels: windowedData.map((d) => formatRuDate(d.date)),
            datasets: [
              {
                label: showCommentSeries ? 'Посты' : marker,
                data: windowedData.map((d) => d.value),
                fill: false,
                borderColor: showCommentSeries ? '#1976d2' : markerColors[marker] || '#1976d2',
                backgroundColor: showCommentSeries ? '#1976d2' : markerColors[marker] || '#1976d2',
                yAxisID: 'y',
                tension: 0.2,
                spanGaps: false,
                clip: false,
                pointBackgroundColor: windowedData.map((d) =>
                  d.isGap ? '#ffcccc' : showCommentSeries ? '#1976d2' : markerColors[marker] || '#1976d2'
                ),
                pointBorderColor: windowedData.map((d) =>
                  d.isGap ? '#ff6666' : showCommentSeries ? '#1976d2' : markerColors[marker] || '#1976d2'
                ),
                pointStyle: windowedData.map((d) =>
                  d.isGap ? 'triangle' : 'circle'
                ),
                segment: {
                  borderDash: (ctx) => {
                    const index = ctx.p0DataIndex;
                    const currentPoint = windowedData[index];
                    const nextPoint = windowedData[index + 1];
                    if (currentPoint?.isGap || nextPoint?.isGap) {
                      return [5, 5];
                    }
                    return [];
                  },
                },
              },
              ...(showCommentSeries ? [{
                label: 'Комментарии и ответы',
                data: windowedData.map((d) => d.commentCount || 0),
                fill: false,
                borderColor: '#d32f2f',
                backgroundColor: '#d32f2f',
                yAxisID: 'yComments',
                tension: 0.2,
                spanGaps: false,
              }] : []),
            ],
          },
        };

        return (
          <div key={marker} style={{ minWidth: '800px', width: '100%', marginBottom: 40 }}>
            <h3 style={{ textAlign: 'center', marginBottom: 12 }}>
              {getMarkerLabelFunction(marker)}
            </h3>
            <div style={{ height: '300px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }} >
            <Line
              data={chart.data}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                plugins: {
                  legend: { display: showCommentSeries },
                  title: { display: false },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        const point = windowedData[context.dataIndex];
                        const baseLabel = `${context.dataset.label}: ${context.parsed.y}`;
                        return point?.isGap ? `${baseLabel} (нет данных)` : baseLabel;
                      },
                    },
                  },
                },
                interaction: { mode: 'nearest', intersect: false },
                onClick: (evt, elements) => {
                  if (!elements.length) return;
                  const { index } = elements[0];
                  const point = windowedData[index];
                  if (point?.date) handlePointClick(point.date, { marker, topicLabel, topicTitle, groupId });
                },
                scales: {
                  x: { title: { display: true, text: 'Дата' } },
                  y: {
                    title: { display: true, text: showCommentSeries ? 'Посты' : 'Значение' },
                    min: extents.min,
                    max: extents.max,
                  },
                  ...(showCommentSeries ? {
                    yComments: {
                      type: 'linear',
                      position: 'right',
                      beginAtZero: true,
                      title: { display: true, text: 'Комментарии и ответы' },
                      grid: { drawOnChartArea: false },
                      ticks: { precision: 0 },
                    },
                  } : {}),
                },
              }}
            />
            </div>
          </div>
        );
      })}
      
      <ChartPointDataModal
        date={selectedDate}
        rows={selectedRows}
        loading={detailsLoading}
        error={detailsError}
        postCards={postCards}
        title={selectedTitle}
        onClose={() => { detailsRequestId.current += 1; setSelectedDate(null); }}
      />
    </>
  );
};

export default ChartsGroup;
